
interface RibbonBrushOptions {
  screenWidth?: number;
  screenHeight?: number;
  strokes?: number;
  refreshRate?: number;
  easing?: number;
  brushSize?: number;
  brushPressure?: number;
  color?: [number, number, number];
  backgroundColor?: [number, number, number];
  role?: "student" | "tutor";
}

interface Painter {
  dx: number;
  dy: number;
  ax: number;
  ay: number;
  div: number;
  ease: number;
}

type SendToFirebaseFn = (action: Action) => void;

interface ImageElement {
  type: "image";
  id: number;
  url: string;
  x: number;
  y: number;
  color: [number, number, number];
  width: number;
  height: number;
  boundingBox: [[number, number], [number, number]];
  isDeleted: boolean;
}
interface TextElement {
  type: "text";
  id: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  color: [number, number, number];
  stroke: number;
  isDeleted: boolean;
}
interface ShapeElement {
  type: "shape";
  id: number;
  shape: string;
  x: number;
  y: number;
  width: number;
  height: number;
  halfWidth: number;
  halfHeight: number;
  color: [number, number, number];
  stroke: number;
  isDeleted: boolean;
}
interface PathElement {
  type: "path";
  id: number;
  color: [number, number, number];
  stroke: number;
  points: [number, number][];
  boundingBox: [[number, number], [number, number]];
  startTime: number;
  endTime: number;
  isDeleted: boolean;
  isAnimated?: boolean;
}

interface Action {
  type: "draw" | "delete" | "update" | "pan" | "path";
  timestamp: number;
  elementId?: number;
  element?: Element;
  cameraPan?: [number, number];
  screenDimensions?: { width: number; height: number };
}

type Element = ImageElement | TextElement | ShapeElement | PathElement;

type Drawing = [number, number][];

type Tool = "pen" | "eraser" | "text" | "shape" | "select" | "hand" | "image";

type ResizingDirection =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";

export class CanvasRibbonBrush {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private options: Required<RibbonBrushOptions>;
  private painters: Painter[] = [];
  private mouseX: number;
  private mouseY: number;
  private DELETE_THRESHOLD: number = 5;
  private deleting: boolean = false;
  private drawing: boolean = false;
  private interval: number | null = null;
  private lastPath: Drawing = [];
  private elements: Element[] = [];
  private deletedElements: (Element & { source: "eraser" | "undo" })[] = [];
  private currentTool: Tool = "pen";
  private panMode: boolean = false;
  private isPanning: boolean = false;
  private panOffsetY: number = 0;
  private cameraOffset: { x: number; y: number } = { x: 0, y: 0 };
  private SCROLL_SENSITIVITY: number = 2;
  private shape: string = "rectangle";
  private sendToFirebase: SendToFirebaseFn | undefined;
  private uploadImgToFirebase: (file: File) => Promise<string> = () => {
    return Promise.resolve("");
  };
  private setCurrentTool: (tool: string) => void;
  private eraserTrail: [number, number][] = []; // Store trail points
  private currentShape: Partial<ShapeElement> | null = null;
  private currentPathStartTime: number = 0;
  private movingElement: Element | null = null;
  private resizingElement: ImageElement | null = null;
  private originalColor: [number, number, number] | null = null;
  private imageCache: { [id: string]: HTMLImageElement } = {};
  private hoveredImage: ImageElement | null = null;
  private resizingDirection: ResizingDirection | null = null;
  private currentCursor: string = "crosshair";
  private tutorScreenDimensions: { width: number; height: number } | null =
    null;
  private wheelThrottleTimer: NodeJS.Timeout | null = null;
  private touchStartTime: number = 0;
  private touchStartPosition: { x: number; y: number } | null = null;
  private TOUCH_TAP_THRESHOLD: number = 10; // pixels
  private TOUCH_TAP_TIME_THRESHOLD: number = 200; // milliseconds

  private defaults: Required<RibbonBrushOptions> = {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    strokes: 1,
    refreshRate: 5,
    easing: 0.7,
    brushSize: 2,
    brushPressure: 1,
    color: [0, 0, 0],
    backgroundColor: [255, 255, 255],
    role: "student",
  };

  constructor(
    canvas: HTMLCanvasElement,
    options: RibbonBrushOptions = {},
    setCurrentTool: (tool: string) => void,
    sendToFirebase?: SendToFirebaseFn,
    uploadImgToFirebase?: (file: File) => Promise<string>
  ) {
    this.options = { ...this.defaults, ...options };
    this.canvas = canvas;

    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context not available");
    this.context = ctx;

    const rect = this.canvas.getBoundingClientRect();

    this.mouseX = rect.left + rect.width / 2;
    this.mouseY = rect.top + rect.height / 2;
    this.cameraOffset.x = rect.left + rect.width / 2;
    this.cameraOffset.y = rect.top + rect.height / 2;
    this.sendToFirebase = sendToFirebase;
    this.setCurrentTool = setCurrentTool;
    if (uploadImgToFirebase) this.uploadImgToFirebase = uploadImgToFirebase;

    this.init();
  }

  private init() {
    const { screenWidth, screenHeight, strokes, easing } = this.options;

    const rect = this.canvas.getBoundingClientRect();

    // if (getHostname() == "localhost") {
    //   this.canvas.width = window.innerWidth;
    //   this.canvas.height = window.innerHeight;
    // } else {
    //   // this.canvas.width = rect.width;
    //   // this.canvas.height = rect.height;
    //   this.canvas.width = window.innerWidth;
    //   this.canvas.height = window.innerHeight;
    // }

    this.canvas.width = rect.width;
    this.canvas.height = rect.height;

    this.canvas.style.backgroundColor = "#f9fafb";
    this.canvas.style.cursor = this.panMode ? "grab" : this.currentCursor;

    for (let i = 0; i < strokes; i++) {
      // const ease = Math.random() * 0.05 + easing;
      const ease = easing;
      this.painters.push({
        dx: screenWidth / 2,
        dy: screenHeight / 2,
        ax: 0,
        ay: 0,
        div: 0.1,
        ease,
      });
    }

    if (this.options.role === "tutor") {
      this.canvas.addEventListener("wheel", this.onWheel.bind(this), {
        passive: false,
      });
      this.canvas.addEventListener("mousedown", this.onMouseDown.bind(this));
      this.canvas.addEventListener("mousemove", this.onMouseMove.bind(this));
      this.canvas.addEventListener("mouseout", this.onMouseUp.bind(this));
      window.addEventListener("mouseup", this.onMouseUp.bind(this));

      // Touch events
      this.canvas.addEventListener("touchstart", (e) => this.onMouseDown(e), {
        passive: false,
      });
      this.canvas.addEventListener("touchmove", (e) => this.onMouseMove(e), {
        passive: false,
      });
      this.canvas.addEventListener("touchend", (e) => this.onMouseUp(e), {
        passive: false,
      });
      this.canvas.addEventListener("touchcancel", (e) => this.onMouseUp(e), {
        passive: false,
      });

      // Prevent default touch behaviors that might interfere
      this.canvas.addEventListener("touchstart", (e) => {
        e.preventDefault(); // Prevent scrolling, zooming, etc.
      }, { passive: false });
      
      this.canvas.addEventListener("touchmove", (e) => {
        e.preventDefault(); // Prevent scrolling during drawing
      }, { passive: false });

      window.addEventListener("keydown", this.onKeyDown.bind(this));
      window.addEventListener("keyup", this.onKeyUp.bind(this));
      // window.addEventListener("resize", this.onResize.bind(this));
    }

    if (this.options.role === "student") {
      this.canvas.style.cursor = "default";
      this.currentCursor = "default";
    }

    // this.onResize();
  }

  private setCursor(cursor: string) {
    this.currentCursor = cursor;
    this.canvas.style.cursor = cursor;
  }

  private addElement(el: Element) {
    this.elements.push(el);
    this.addAction({
      type: "draw",
      timestamp: Date.now(),
      elementId: el.id,
      element: { ...el, isAnimated: el.type === "path" } as Element,
      screenDimensions: {
        width: this.canvas.clientWidth,
        height: this.canvas.clientHeight,
      },
    });
    localStorage.setItem("canvasElements", JSON.stringify(this.elements));
  }

  private startDrawing() {
    if (!this.interval) {
      this.interval = window.setInterval(
        () => this.update(),
        this.options.refreshRate
      );
    }
  }

  private stopDrawing() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
      if (this.options.role === "tutor") {
        this.lastPath = [];
      }
    }
  }

  private update(p?: { x: number; y: number }) {
    const ctx = this.context;
    const opts = this.options;

    const mouseX = typeof p?.x === "number" ? p?.x : this.mouseX;
    const mouseY = typeof p?.y === "number" ? p?.y : this.mouseY;

    ctx.lineWidth = opts.brushSize;
    ctx.strokeStyle = `rgba(${opts.color[0]}, ${opts.color[1]}, ${opts.color[2]}, ${opts.brushPressure})`;

    for (let i = 0; i < this.painters.length; i++) {
      ctx.beginPath();

      const p = this.painters[i];
      ctx.moveTo(p.dx, p.dy);

      p.ax = (p.ax + (p.dx - mouseX) * p.div) * p.ease;
      p.dx -= p.ax;
      p.ay = (p.ay + (p.dy - mouseY) * p.div) * p.ease;
      p.dy -= p.ay;

      ctx.lineTo(p.dx, p.dy);
      ctx.stroke();

      if (this.options.role === "tutor") {
        this.lastPath.push([p.dx, p.dy - this.panOffsetY]);
      }
    }
  }

  private onMouseDown(e: MouseEvent | TouchEvent) {
    // Prevent multi-touch interference on touch devices
    if (e instanceof TouchEvent && e.touches.length > 1) {
      e.preventDefault();
      return;
    }
    
    // Track touch start for tap detection
    if (e instanceof TouchEvent) {
      this.touchStartTime = Date.now();
      const [x, y] = this.getMousePosition(e);
      this.touchStartPosition = { x, y };
    }
    
    if (this.panMode) {
      this.isPanning = true;
      this.setCursor("grabbing");
      const [mouseX, mouseY] = this.getMousePosition(e);
      this.mouseX = mouseX;
      this.mouseY = mouseY;
    } else if (this.currentTool === "select") {
      const [mouseX, mouseY] = this.getMousePosition(e);

      this.mouseX = mouseX;
      this.mouseY = mouseY;

      // Check if an element is hovered
      for (const el of this.elements) {
        let isCollision = false;

        if (el.type === "shape") {
          if (el.shape === "circle") {
            const radius = el.halfWidth!;
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const distance = Math.sqrt(
              Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2)
            );
            isCollision = distance <= radius;
          } else if (el.shape === "rectangle") {
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;
            isCollision =
              mouseX > x - halfWidth &&
              mouseX < x + halfWidth &&
              mouseY > y - halfHeight &&
              mouseY < y + halfHeight;
          } else if (el.shape === "triangle") {
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;
            isCollision =
              mouseX > x - halfWidth &&
              mouseX < x + halfWidth &&
              mouseY > y - halfHeight &&
              mouseY < y + halfHeight;
          }
        } else if (el.type === "path") {
          for (const point of el.points) {
            const distance = Math.sqrt(
              Math.pow(point[0] - mouseX, 2) +
                Math.pow(point[1] + this.panOffsetY - mouseY, 2)
            );
            if (distance < this.DELETE_THRESHOLD) {
              isCollision = true;
              break;
            }
          }
        } else if (el.type === "text") {
          const x = el.x!;
          const y = el.y! + this.panOffsetY;
          const width = el.width!;
          const height = el.height!;
          isCollision =
            mouseX > x &&
            mouseX < x + width &&
            mouseY > y &&
            mouseY < y + height;
        } else if (el.type === "image") {
          const x = el.x!;
          const y = el.y! + this.panOffsetY;
          const width = el.width!;
          const height = el.height!;
          isCollision =
            mouseX > x &&
            mouseX < x + width &&
            mouseY > y &&
            mouseY < y + height;
          // For touch events, don't immediately set resizing - let the move handler determine this
          // This prevents accidental resizing when just trying to select/move
        }

        if (isCollision) {
          this.movingElement = el;
          break;
        }
      }
    } else if (this.currentTool === "text") {
      const [mouseX, mouseY] = this.getMousePosition(e);
      const x = mouseX;
      const y = mouseY - this.panOffsetY;

      // Check if clicking on an existing text element
      const clickedText = this.elements.find(
        (el) =>
          el.type === "text" &&
          x >= el.x &&
          x <= el.x + el.width &&
          y >= el.y &&
          y <= el.y + el.height
      ) as TextElement;

      if (clickedText) {
        // Edit existing text - pass screen coordinates for input positioning
        this.editText(clickedText, { x: mouseX, y: mouseY });
      } else {
        // Create new text element
        this.createTextElement(x, y, { x: mouseX, y: mouseY });
      }
    } else if (this.currentTool === "pen") {
      this.drawing = true;
      const [mouseX, mouseY] = this.getMousePosition(e);

      this.mouseX = mouseX;
      this.mouseY = mouseY;

      for (const p of this.painters) {
        p.dx = mouseX;
        p.dy = mouseY;
      }
      let startTime = Date.now();
      this.currentPathStartTime = startTime;
      this.startDrawing();
    } else if (this.currentTool === "eraser") {
      this.deleting = true;
    } else if (this.currentTool === "shape") {
      const [x, y] = this.getMousePosition(e);

      this.currentShape = {
        type: "shape",
        id: Date.now(),
        shape: this.shape,
        x,
        y,
        width: 0,
        height: 0,
        halfWidth: 0,
        halfHeight: 0,
        color: [...this.options.color],
        stroke: this.options.brushSize,
      };
    } else if (this.currentTool === "image") {
      if (this.hoveredImage) {
        this.resizingElement = this.hoveredImage;
      } else if (e instanceof TouchEvent) {
        // Handle touch-specific image interaction
        const [x, y] = this.getMousePosition(e);
        const canvasY = y - this.panOffsetY;
        
        // Find if touching an existing image
        let touchedImage: ImageElement | null = null;
        for (const el of this.elements) {
          if (el.type === "image") {
            const isCollision = x >= el.x && 
                               x <= el.x + el.width && 
                               canvasY >= el.y && 
                               canvasY <= el.y + el.height;
            if (isCollision) {
              touchedImage = el;
              break;
            }
          }
        }
        
        if (touchedImage) {
          // For touch devices, immediately start resizing when touching an image
          this.resizingElement = touchedImage;
          this.hoveredImage = touchedImage;
          // Set a default resize direction for touch (usually bottom-right)
          this.resizingDirection = "bottomRight";
        } else {
          // Create new image if not touching existing one
          this.insertImage({ x, y });
        }
      } else {
        const [x, y] = this.getMousePosition(e);
        this.insertImage({ x, y });
      }
    }
  }

  private onMouseMove(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    
    // Prevent multi-touch interference on touch devices
    if (e instanceof TouchEvent && e.touches.length > 1) {
      return;
    }
    
    if (this.isPanning) {
      this.pan(e);
    } else if (this.currentTool === "select") {
      const [mouseX, mouseY] = this.getMousePosition(e);
      let movement = { x: 0, y: 0 };

      if (e instanceof MouseEvent) {
        movement = {
          x: e.movementX,
          y: e.movementY,
        };
      } else if (e instanceof TouchEvent) {
        const touch = e.touches[0];
        movement = {
          x: touch.clientX - this.mouseX,
          y: touch.clientY - this.mouseY,
        };
        this.mouseX = touch.clientX;
        this.mouseY = touch.clientY;
      }

      if (this.movingElement) {
        // Update the position of the moving element
        if (this.movingElement.type === "shape") {
          this.movingElement.x += movement.x;
          this.movingElement.y += movement.y;
        } else if (this.movingElement.type === "path") {
          this.movingElement.points = this.movingElement.points.map(
            ([x, y]) => [x + movement.x, y + movement.y]
          );
        } else if (this.movingElement.type === "text") {
          this.movingElement.x += movement.x;
          this.movingElement.y += movement.y;
        } else if (this.movingElement.type === "image") {
          this.movingElement.x += movement.x;
          this.movingElement.y += movement.y;
        }
        this.redraw();
        return;
      }

      // Detect collision with elements
      let hoveredElement: Element | null = null;
      for (const el of this.elements) {
        let isCollision = false;

        if (el.type === "shape") {
          if (el.shape === "circle") {
            const radius = el.halfWidth!;
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const distance = Math.sqrt(
              Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2)
            );
            if (distance <= radius) {
              isCollision = true;
            }
          } else if (el.shape === "rectangle") {
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;

            const minX = x - halfWidth;
            const minY = y - halfHeight;
            const maxX = x + halfWidth;
            const maxY = y + halfHeight;
            if (
              mouseX > minX &&
              mouseX < maxX &&
              mouseY > minY &&
              mouseY < maxY
            ) {
              isCollision = true;
            }
          } else if (el.shape === "triangle") {
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;

            const minX = x - halfWidth;
            const minY = y - halfHeight;
            const maxX = x + halfWidth;
            const maxY = y + halfHeight;
            if (
              mouseX > minX &&
              mouseX < maxX &&
              mouseY > minY &&
              mouseY < maxY
            ) {
              isCollision = true;
            }
          }
        } else if (el.type === "path") {
          for (const point of el.points) {
            const distance = Math.sqrt(
              Math.pow(point[0] - mouseX, 2) +
                Math.pow(point[1] + this.panOffsetY - mouseY, 2)
            );
            if (distance < this.DELETE_THRESHOLD) {
              isCollision = true;
              break;
            }
          }
        } else if (el.type === "text" || el.type === "image") {
          const x = el.x!;
          const y = el.y! + this.panOffsetY;
          const width = el.width!;
          const height = el.height!;
          if (
            mouseX > x &&
            mouseX < x + width &&
            mouseY > y &&
            mouseY < y + height
          ) {
            isCollision = true;
          }
        }

        if (isCollision) {
          hoveredElement = el;
          break;
        }
      }

      // Change color of hovered element
      if (hoveredElement) {
        if (!this.movingElement && this.originalColor === null) {
          this.originalColor = [...hoveredElement.color];
          hoveredElement.color = [173, 216, 230]; // Light blue
          this.redraw();
        }
      } else if (this.originalColor) {
        // Reset color if no element is hovered
        const element = this.elements.find(
          (el) =>
            el.color[0] === 173 && el.color[1] === 216 && el.color[2] === 230
        ) as Element | undefined;
        if (element) {
          element.color = [...this.originalColor];
          this.originalColor = null;
          this.redraw();
        }
      }
    } else if (this.currentTool === "pen") {
      if (!this.drawing) return;
      const [mouseX, mouseY] = this.getMousePosition(e);

      this.mouseX = mouseX;
      this.mouseY = mouseY;
    } else if (this.currentTool === "eraser") {
      if (!this.deleting) return;
      const [mouseX, mouseY] = this.getMousePosition(e);

      // Add the current mouse position to the trail
      this.eraserTrail.push([mouseX, mouseY]);

      // Limit the trail length for performance
      if (this.eraserTrail.length > 10) {
        this.eraserTrail.shift();
      }

      // Redraw the canvas with the trail
      this.redraw();
      this.drawEraserTrail();

      for (const el of this.elements) {
        let isCollision = false;
        // Dynamically calculate the transformed bounding box
        if (el.type === "shape") {
          if (el.shape === "circle") {
            const radius = el.halfWidth!;
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const distance = Math.sqrt(
              Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2)
            );
            if (Math.abs(distance - radius) <= this.DELETE_THRESHOLD) {
              isCollision = true;
            }
          } else if (el.shape === "triangle") {
            // TODO: Add collision detection for triangles
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;
            const threshold = 100;

            const minX = x - halfWidth - threshold;
            const minY = y - halfHeight - threshold;
            const maxX = x + halfWidth + threshold;
            const maxY = y + halfHeight + threshold;
            if (
              mouseX > minX &&
              mouseX < maxX &&
              mouseY > minY &&
              mouseY < maxY
            ) {
              const p1 = [x, y - halfHeight];
              const p2 = [x - halfWidth, y + halfHeight];
              const p3 = [x + halfWidth, y + halfHeight];
              // (x−x1)(y2−y1)−(y−y1)(x2−x1)=0
              const collinear1 =
                (mouseX - p1[0]) * (p2[1] - p1[1]) -
                (mouseY - p1[1]) * (p2[0] - p1[0]);

              const collinear2 =
                (mouseX - p2[0]) * (p3[1] - p2[1]) -
                (mouseY - p2[1]) * (p3[0] - p2[0]);

              const collinear3 =
                (mouseX - p3[0]) * (p1[1] - p3[1]) -
                (mouseY - p3[1]) * (p1[0] - p3[0]);

              if (
                Math.abs(collinear1) < threshold ||
                Math.abs(collinear2) < threshold ||
                Math.abs(collinear3) < threshold
              ) {
                isCollision = true;
              }
            }
          } else if (el.shape === "rectangle") {
            const x = el.x!;
            const y = el.y! + this.panOffsetY;
            const halfWidth = el.halfWidth!;
            const halfHeight = el.halfHeight!;
            const threshold = 50;

            const minX = x - halfWidth - threshold;
            const minY = y - halfHeight - threshold;
            const maxX = x + halfWidth + threshold;
            const maxY = y + halfHeight + threshold;
            if (
              mouseX > minX &&
              mouseX < maxX &&
              mouseY > minY &&
              mouseY < maxY
            ) {
              const topLeft = [x - halfWidth, y - halfHeight];
              const bottomLeft = [x - halfWidth, y + halfHeight];
              const topRight = [x + halfWidth, y - halfHeight];
              const bottomRight = [x + halfWidth, y + halfHeight];
              // (x−x1)(y2−y1)−(y−y1)(x2−x1)=0
              const collinear1 =
                (mouseX - topLeft[0]) * (bottomLeft[1] - topLeft[1]) -
                (mouseY - topLeft[1]) * (bottomLeft[0] - topLeft[0]);

              const collinear2 =
                (mouseX - topLeft[0]) * (topRight[1] - topLeft[1]) -
                (mouseY - topLeft[1]) * (topRight[0] - topLeft[0]);

              const collinear3 =
                (mouseX - topRight[0]) * (bottomRight[1] - topRight[1]) -
                (mouseY - topRight[1]) * (bottomRight[0] - topRight[0]);

              const collinear4 =
                (mouseX - bottomRight[0]) * (bottomLeft[1] - bottomRight[1]) -
                (mouseY - bottomRight[1]) * (bottomLeft[0] - bottomRight[0]);

              if (
                Math.abs(collinear1) < threshold ||
                Math.abs(collinear2) < threshold ||
                Math.abs(collinear3) < threshold ||
                Math.abs(collinear4) < threshold
              ) {
                isCollision = true;
              }
            }
          }
        } else if (el.type === "path") {
          const [minX, maxX] = [el.boundingBox[0][0], el.boundingBox[0][1]];
          const [minY, maxY] = [
            el.boundingBox[1][0] + this.panOffsetY,
            el.boundingBox[1][1] + this.panOffsetY,
          ];

          if (
            mouseX > minX &&
            mouseX < maxX &&
            mouseY > minY &&
            mouseY < maxY
          ) {
            for (const point of el.points) {
              const distance = Math.sqrt(
                Math.pow(point[0] - mouseX, 2) +
                  Math.pow(point[1] + this.panOffsetY - mouseY, 2)
              );
              if (distance < this.DELETE_THRESHOLD) {
                isCollision = true;
                break;
              }
            }
          }
        } else if (el.type === "text") {
          const x = el.x!;
          const y = el.y! + this.panOffsetY;
          const width = el.width!;
          const height = el.height!;
          if (
            mouseX > x &&
            mouseX < x + width &&
            mouseY > y &&
            mouseY < y + height
          ) {
            isCollision = true;
          }
        } else if (el.type === "image") {
          const x = el.x!;
          const y = el.y! + this.panOffsetY;
          const width = el.width!;
          const height = el.height!;

          const image = new Path2D();
          image.rect(x, y, width, height);
          isCollision = this.context.isPointInStroke(image, mouseX, mouseY);
        }
        if (isCollision) {
          this.elements.forEach((e) => {
            if (e.id === el.id) {
              e.isDeleted = true;
              this.deletedElements.push({ ...el, source: "eraser" });
            }
          });
          this.addAction({
            type: "delete",
            timestamp: Date.now(),
            elementId: el.id,
            screenDimensions: {
              width: this.canvas.clientWidth,
              height: this.canvas.clientHeight,
            },
          });
          this.redraw();
          break;
        }
      }
    } else if (this.currentTool === "shape" && this.currentShape) {
      const [x, y] = this.getMousePosition(e);
      this.currentShape.halfWidth = Math.abs(x - this.currentShape.x!);
      this.currentShape.halfHeight = Math.abs(y - this.currentShape.y!);
      this.redraw();
      this.drawShape(this.currentShape);
    } else if (this.currentTool === "image") {
      const [mouseX, mouseY] = this.getMousePosition(e);
      const x = mouseX;
      const y = mouseY - this.panOffsetY;

      // Only do hover detection for mouse events (not touch)
      if (e instanceof MouseEvent) {
        for (const el of this.elements) {
          if (el.type === "image") {
            const image = new Path2D();
            image.rect(el.x, el.y, el.width, el.height);
            const isCollision = this.context.isPointInStroke(image, x, y);
            if (isCollision && !this.resizingElement) {
              this.hoveredImage = el;
              // Calculate which edge or corner of the image is hovered
              const imgX = el.x;
              const imgY = el.y + this.panOffsetY;
              const width = el.width;
              const height = el.height;
              const threshold = 50;
              
              // Corner and edge detection logic for mouse cursor changes
              const topLeft = [imgX, imgY];
              const topRight = [imgX + width, imgY];
              const bottomLeft = [imgX, imgY + height];
              const bottomRight = [imgX + width, imgY + height];
              
              const topLeftCornerDistance = Math.sqrt(
                Math.pow(mouseX - topLeft[0], 2) +
                Math.pow(mouseY - topLeft[1], 2)
              );
              if (topLeftCornerDistance < threshold) {
                this.canvas.style.cursor = "nwse-resize";
                this.resizingDirection = "topLeft";
                break;
              }
              
              const topRightCornerDistance = Math.sqrt(
                Math.pow(mouseX - topRight[0], 2) +
                Math.pow(mouseY - topRight[1], 2)
              );
              if (topRightCornerDistance < threshold) {
                this.canvas.style.cursor = "nesw-resize";
                this.resizingDirection = "topRight";
                break;
              }
              
              const bottomLeftCornerDistance = Math.sqrt(
                Math.pow(mouseX - bottomLeft[0], 2) +
                Math.pow(mouseY - bottomLeft[1], 2)
              );
              if (bottomLeftCornerDistance < threshold) {
                this.canvas.style.cursor = "nesw-resize";
                this.resizingDirection = "bottomLeft";
                break;
              }
              
              const bottomRightCornerDistance = Math.sqrt(
                Math.pow(mouseX - bottomRight[0], 2) +
                Math.pow(mouseY - bottomRight[1], 2)
              );
              if (bottomRightCornerDistance < threshold) {
                this.canvas.style.cursor = "nwse-resize";
                this.resizingDirection = "bottomRight";
                break;
              }
              
              const topCollinear =
                (mouseX - topLeft[0]) * (topRight[1] - topLeft[1]) -
                (mouseY - topLeft[1]) * (topRight[0] - topLeft[0]);
              if (Math.abs(topCollinear) < threshold) {
                this.canvas.style.cursor = "ns-resize";
                this.resizingDirection = "top";
                break;
              }
              
              const rightCollinear =
                (mouseX - topRight[0]) * (bottomRight[1] - topRight[1]) -
                (mouseY - topRight[1]) * (bottomRight[0] - topRight[0]);
              if (Math.abs(rightCollinear) < threshold) {
                this.canvas.style.cursor = "ew-resize";
                this.resizingDirection = "right";
                break;
              }
              
              const bottomCollinear =
                (mouseX - bottomLeft[0]) * (bottomRight[1] - bottomLeft[1]) -
                (mouseY - bottomLeft[1]) * (bottomRight[0] - bottomLeft[0]);
              if (Math.abs(bottomCollinear) < threshold) {
                this.canvas.style.cursor = "ns-resize";
                this.resizingDirection = "bottom";
                break;
              }
              
              const leftCollinear =
                (mouseX - bottomLeft[0]) * (topLeft[1] - bottomLeft[1]) -
                (mouseY - bottomLeft[1]) * (topLeft[0] - bottomLeft[0]);
              if (Math.abs(leftCollinear) < threshold) {
                this.canvas.style.cursor = "ew-resize";
                this.resizingDirection = "left";
                break;
              }
            } else {
              this.hoveredImage = null;
              this.canvas.style.cursor = this.currentCursor;
            }
          }
        }
      }

      if (this.resizingElement) {
        let movement = { x: 0, y: 0 };

        if (e instanceof MouseEvent) {
          movement = {
            x: e.movementX,
            y: e.movementY,
          };
        } else if (e instanceof TouchEvent) {
          const touch = e.touches[0];
          movement = {
            x: touch.clientX - this.mouseX,
            y: touch.clientY - this.mouseY,
          };
          this.mouseX = touch.clientX;
          this.mouseY = touch.clientY;
        }

        if (this.resizingDirection === "topLeft") {
          this.resizingElement.x += movement.x;
          this.resizingElement.width -= movement.x;
          this.resizingElement.y += movement.y;
          this.resizingElement.height -= movement.y;
        } else if (this.resizingDirection === "topRight") {
          this.resizingElement.width += movement.x;
          this.resizingElement.y += movement.y;
          this.resizingElement.height -= movement.y;
        } else if (this.resizingDirection === "bottomLeft") {
          this.resizingElement.x += movement.x;
          this.resizingElement.width -= movement.x;
          this.resizingElement.height += movement.y;
        } else if (this.resizingDirection === "bottomRight") {
          this.resizingElement.width += movement.x;
          this.resizingElement.height += movement.y;
        } else if (this.resizingDirection === "top") {
          this.resizingElement.y += movement.y;
          this.resizingElement.height -= movement.y;
        } else if (this.resizingDirection === "right") {
          this.resizingElement.width += movement.x;
        } else if (this.resizingDirection === "bottom") {
          this.resizingElement.height += movement.y;
        } else if (this.resizingDirection === "left") {
          this.resizingElement.x += movement.x;
          this.resizingElement.width -= movement.x;
        }

        this.redraw();
      }
    }
  }

  private onMouseUp(e?: MouseEvent | TouchEvent) {
    // Handle touch tap detection for better touch interaction
    let isTouchTap = false;
    if (e instanceof TouchEvent && this.touchStartPosition && this.touchStartTime) {
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - this.touchStartTime;
      
      if (touchDuration <= this.TOUCH_TAP_TIME_THRESHOLD) {
        const [endX, endY] = this.getMousePosition(e);
        const distance = Math.sqrt(
          Math.pow(endX - this.touchStartPosition.x, 2) +
          Math.pow(endY - this.touchStartPosition.y, 2)
        );
        
        if (distance <= this.TOUCH_TAP_THRESHOLD) {
          isTouchTap = true;
        }
      }
      
      // Reset touch tracking
      this.touchStartTime = 0;
      this.touchStartPosition = null;
    }
    
    if (this.panMode) {
      this.addAction({
        type: "pan",
        timestamp: Date.now(),
        cameraPan: [0, this.panOffsetY],
      });
      this.isPanning = false;
      this.setCursor("grab");
    } else if (this.currentTool === "select") {
      this.setCursor("default");
      if (this.movingElement) {
        // Reset the color of the moving element
        if (this.originalColor && this.movingElement.type !== "image") {
          this.movingElement.color = [...this.originalColor];
          this.originalColor = null;
        }

        this.addAction({
          type: "update",
          timestamp: Date.now(),
          elementId: this.movingElement.id,
          element: this.movingElement,
        });

        this.movingElement = null;
        this.redraw();
      }
    } else if (this.currentTool === "pen") {
      this.setCursor("crosshair");
      this.drawing = false;

      if (this.lastPath?.length > 0) {
        const [minX, maxX] = [
          Math.min(...this.lastPath.map((p) => p[0])),
          Math.max(...this.lastPath.map((p) => p[0])),
        ];
        const [minY, maxY] = [
          Math.min(...this.lastPath.map((p) => p[1])),
          Math.max(...this.lastPath.map((p) => p[1])),
        ];
        let timestamp = Date.now();
        this.addElement({
          type: "path",
          id: this.currentPathStartTime,
          startTime: this.currentPathStartTime,
          endTime: timestamp,
          color: [...this.options.color],
          stroke: this.options.brushSize,
          points: this.lastPath,
          boundingBox: [
            [minX, maxX],
            [minY, maxY],
          ],
          isDeleted: false,
        } as PathElement);
      }
      this.stopDrawing();
    } else if (this.currentTool === "eraser") {
      this.setCursor("default");
      this.deleting = false;
      this.eraserTrail = []; // Clear the trail
      this.redraw(); // Redraw the canvas to remove the trail
    } else if (this.currentTool === "shape" && this.currentShape) {
      this.setCursor("crosshair");
      this.currentShape = {
        ...this.currentShape,
        x: this.currentShape.x! - 0 - this.panOffsetY,
        y: this.currentShape.y! - this.panOffsetY,
        width: this.currentShape.halfWidth! * 2,
        height: this.currentShape.halfHeight! * 2,
        halfWidth: this.currentShape.halfWidth!,
        halfHeight: this.currentShape.halfHeight!,
        isDeleted: false,
      };
      this.addElement(this.currentShape as ShapeElement);
      this.currentShape = null;
      this.redraw();
    } else if (this.currentTool === "image") {
      if (this.resizingElement) {
        this.addAction({
          type: "update",
          timestamp: Date.now(),
          elementId: this.resizingElement.id,
          element: this.resizingElement,
        });
        this.resizingElement = null;
        this.hoveredImage = null;
      }
    }
  }

  private onWheel(e: WheelEvent) {
    e.preventDefault();

    // Only handle vertical scrolling (panning)
    const deltaY = e.deltaY * this.SCROLL_SENSITIVITY;
    this.panOffsetY -= deltaY;

    this.redraw();

    // Throttle Firebase calls to prevent spam
    if (this.wheelThrottleTimer) {
      clearTimeout(this.wheelThrottleTimer);
    }

    this.wheelThrottleTimer = setTimeout(() => {
      // Send pan action to Firebase with screen dimensions
      this.addAction({
        type: "pan",
        timestamp: Date.now(),
        cameraPan: [0, this.panOffsetY],
        screenDimensions: {
          width: this.canvas.clientWidth,
          height: this.canvas.clientHeight,
        },
      });
    }, 1000); // Wait 1s after last wheel event
  }

  private createTextElement(
    x: number,
    y: number,
    mouse: { x: number; y: number }
  ) {
    this.stopEditingText();
    const newText: TextElement = {
      type: "text",
      id: Date.now(),
      text: "",
      x,
      y,
      width: 0,
      height: 0,
      fontSize: this.options.brushSize * 6,
      color: [...this.options.color],
      stroke: 1,
      isDeleted: false,
    };

    this.elements.push(newText);
    this.editText(newText, mouse);
  }

  private editText(textElement: TextElement, mouse: { x: number; y: number }) {
    this.elements = this.elements.filter((el) => el.id !== textElement.id);
    this.redraw();

    const input = document.createElement("input");
    input.type = "text";
    input.value = textElement.text;
    input.style.position = "absolute";
    // input.style.left = `${mouse.x}px`;
    // input.style.top = `${mouse.y - (input.height || 5)}px`;
    // Get canvas position and calculate absolute screen position
    const canvasRect = this.canvas.getBoundingClientRect();
    const absoluteX = canvasRect.left + mouse.x;
    const absoluteY =
      canvasRect.top + mouse.y - (input.offsetHeight || textElement.fontSize);

    input.style.left = `${absoluteX}px`;
    input.style.top = `${absoluteY}px`;
    input.style.fontSize = `${textElement.fontSize}px`;
    input.style.fontFamily = "Arial";
    input.style.color = `rgb(${textElement.color.join(",")})`;
    input.style.width = "50px";
    input.style.outline = "1px solid black";
    input.style.border = "none";
    input.style.background = "transparent";
    input.style.zIndex = "99999999";
    input.addEventListener("keyup", () => {
      input.style.width = `${
        this.options.brushSize * 3 * input.value.length
      }px`;
    });

    document.body.appendChild(input);
    input.click();

    const finishEditing = () => {
      // Convert screen coordinates back to canvas coordinates
      const canvasRect = this.canvas.getBoundingClientRect();
      const inputRect = input.getBoundingClientRect();
      const canvasX = inputRect.left - canvasRect.left;
      const canvasY = inputRect.top - canvasRect.top;

      textElement.text = input.value;
      textElement.x = canvasX + inputRect.width;
      textElement.y = canvasY - this.panOffsetY; // Account for pan offset
      textElement.width = input.offsetWidth;
      textElement.height = input.offsetHeight;
      textElement.fontSize = this.options.brushSize * 6;
      textElement.color = this.options.color;
      document.body.removeChild(input);
      this.elements.push(textElement);
      this.addAction({
        type: "draw",
        timestamp: Date.now(),
        elementId: textElement.id,
        element: textElement,
      });
      this.redraw();
    };

    input.addEventListener("blur", finishEditing);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        finishEditing();
      }
    });
  }

  private stopEditingText() {
    const activeInput = document.querySelector("input[type='text']");
    if (activeInput) {
      (activeInput as HTMLInputElement).blur();
    }
  }

  private onKeyDown(e: KeyboardEvent) {
    const isTyping = !!document.querySelector('input[type="text"]');
    if (e.key === "Escape" && this.currentTool === "text") {
      this.stopEditingText();
    } else if (e.code === "Space") {
      this.panMode = true;
      this.canvas.style.cursor = "grab";
    } else if (e.key === "z" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (e.shiftKey) {
        this.redo();
      } else {
        this.undo();
      }
    } else if (e.key === "p" && !isTyping) {
      this.currentTool = "pen";
      this.setCursor("crosshair");
      this.setCurrentTool("pen");
    } else if (e.key === "h" && !isTyping) {
      this.currentTool = "hand";
      this.setCursor("grab");
      this.setCurrentTool("hand");
    } else if (e.key === "s" && !isTyping) {
      this.currentTool = "select";
      this.setCursor("default");
      this.setCurrentTool("select");
    } else if (e.key === "t" && !isTyping) {
      this.currentTool = "text";
      this.setCursor("text");
      this.setCurrentTool("text");
    } else if (e.key === "e" && !isTyping) {
      this.currentTool = "eraser";
      this.setCursor("default");
      this.setCurrentTool("eraser");
    } else if (e.key === "i" && !isTyping) {
      this.currentTool = "image";
      this.setCursor("crosshair");
      this.setCurrentTool("image");
    } else if (e.key === "u" && !isTyping) {
      this.currentTool = "shape";
      this.setCursor("default");
      this.setCurrentTool("shape");
    }
  }

  private onKeyUp(e: KeyboardEvent) {
    if (e.code === "Space") {
      this.panMode = false;
      this.isPanning = false;
      this.canvas.style.cursor = this.currentCursor;
    }
  }

  private getMousePosition(e?: MouseEvent | TouchEvent) {
    const rect = this.canvas.getBoundingClientRect();
    let mouseX = rect.left + rect.width / 2;
    let mouseY = rect.top + rect.height / 2;
    if (e instanceof MouseEvent) {
      if (e) {
        mouseX = e.clientX - rect.left;
        mouseY = e.clientY - rect.top;
      }
    } else if (e instanceof TouchEvent) {
      if (e) {
        const touch = e.touches[0] || e.changedTouches[0];
        mouseX = touch.clientX - rect.left;
        mouseY = touch.clientY - rect.top;
      }
    }
    return [mouseX, mouseY];
  }

  private pan(e: MouseEvent | TouchEvent) {
    let movementX = 0;
    let movementY = 0;

    if (e instanceof MouseEvent) {
      // Use movementX and movementY for mouse events
      movementX = e.movementX;
      movementY = e.movementY;
    } else if (e instanceof TouchEvent) {
      // Calculate movement manually for touch events
      const touch = e.touches[0] || e.changedTouches[0];
      const [currentX, currentY] = [touch.clientX, touch.clientY];

      if (this.mouseX !== undefined && this.mouseY !== undefined) {
        movementX = currentX - this.mouseX;
        movementY = currentY - this.mouseY;
      }

      // Update the last known touch position
      this.mouseX = currentX;
      this.mouseY = currentY;
    }

    // Update pan offsets (only vertical)
    // For touch devices, apply a slight damping to make panning feel smoother
    const dampingFactor = e instanceof TouchEvent ? 1.2 : 1.0;
    this.panOffsetY += movementY * dampingFactor;

    // Redraw the canvas
    this.redraw();
  }

  private redraw() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.save();
    this.context.translate(0, this.panOffsetY);

    for (const el of this.elements) {
      if (el.isDeleted) continue;
      if (el.type === "path") {
        this.drawSmoothPath(el);
      } else if (el.type === "shape") {
        this.drawShape(el);
      } else if (el.type === "text") {
        this.drawText(el);
      } else if (el.type === "image") {
        this.drawImage(el);
      }
    }
    this.context.restore();
  }

  private drawText(textElement: TextElement) {
    this.context.font = `${textElement.fontSize}px Arial`;
    this.context.fillStyle = `rgb(${textElement.color.join(",")})`;
    this.context.textBaseline = "top";
    this.context.fillText(textElement.text, textElement.x, textElement.y);
  }

  private drawSmoothPath(el: PathElement) {
    const ctx = this.context;
    const opts = this.options;
    const points = el.points;
    const color = el.color;
    const stroke = el.stroke;

    if (points.length < 2) return;

    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);

    for (let i = 1; i < points.length - 1; i++) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[i + 1];

      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;

      ctx.quadraticCurveTo(x1, y1, cx, cy);
    }

    // Ensure the final segment draws to the last point
    const last = points[points.length - 1];
    ctx.lineTo(last[0], last[1]);

    ctx.lineWidth = stroke;
    ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opts.brushPressure})`;
    ctx.stroke();
  }

  private drawAnimatedSmoothPath(el: PathElement) {
    return new Promise((resolve) => {
      const ctx = this.context;
      const opts = this.options;
      const points = el.points;
      const color = el.color;
      const stroke = el.stroke;

      if (points.length < 2) return;

      // Apply the same transform as redraw() method
      ctx.save();
      ctx.translate(0, this.panOffsetY);

      ctx.beginPath();
      ctx.moveTo(points[0][0], points[0][1]);

      const pointsCount = points.length;
      const pointDelay = (el.endTime - el.startTime) / pointsCount;

      ctx.lineWidth = stroke;
      ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opts.brushPressure})`;

      for (let i = 1; i < points.length - 1; i++) {
        setTimeout(() => {
          const [x1, y1] = points[i];
          const [x2, y2] = points[i + 1];

          const cx = (x1 + x2) / 2;
          const cy = (y1 + y2) / 2;

          ctx.quadraticCurveTo(x1, y1, cx, cy);
          ctx.stroke();
        }, i * pointDelay);
      }

      // Ensure the final segment draws to the last point
      const last = points[points.length - 1];
      setTimeout(() => {
        const [x, y] = last;

        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.restore(); // Restore the transform
        resolve(true);
      }, (pointsCount + 1) * pointDelay);
    });
  }

  private drawImage(imageElement: ImageElement) {
    let img = this.imageCache[imageElement.id];
    if (!img) {
      img = new Image();
      img.src = imageElement.url;
      this.imageCache[imageElement.id] = img;
      img.onload = () => {
        this.redraw(); // Redraw once loaded (only needed the first time)
      };
      img.onerror = () => {
        console.error("Failed to load image:", imageElement.url);
      };
      return; // Wait for image to load before drawing
    }

    // Draw immediately if cached and loaded
    if (img.complete) {
      const adjustedX = imageElement.x;
      const adjustedY = imageElement.y;
      const adjustedWidth = imageElement.width;
      const adjustedHeight = imageElement.height;

      this.context.drawImage(
        img,
        adjustedX,
        adjustedY,
        adjustedWidth,
        adjustedHeight
      );

      // Draw a stroke (border) if desired
      if (imageElement.color[0] !== 0) {
        this.context.save();
        this.context.strokeStyle = `rgba(${imageElement.color[0]}, ${imageElement.color[1]}, ${imageElement.color[2]}, ${this.options.brushPressure})`;
        this.context.lineWidth = 5; // or any width you want
        this.context.strokeRect(
          adjustedX,
          adjustedY,
          adjustedWidth,
          adjustedHeight
        );
        this.context.restore();
      }
    }
  }

  private drawShape(shape: Partial<ShapeElement>) {
    const ctx = this.context;
    ctx.save();
    ctx.strokeStyle = `rgba(${shape.color![0]}, ${shape.color![1]}, ${
      shape.color![2]
    }, ${this.options.brushPressure})`;
    ctx.lineWidth = shape.stroke!;

    if (shape.shape === "rectangle") {
      ctx.strokeRect(
        shape.x! - shape.halfWidth!,
        shape.y! - shape.halfHeight!,
        shape.halfWidth! * 2,
        shape.halfHeight! * 2
      );
    } else if (shape.shape === "circle") {
      const radius = shape.halfWidth!;
      ctx.beginPath();
      ctx.arc(shape.x!, shape.y!, radius, 0, Math.PI * 2);
      ctx.stroke();
    } else if (shape.shape === "triangle") {
      ctx.beginPath();
      ctx.moveTo(shape.x!, shape.y! - shape.halfHeight!);
      ctx.lineTo(shape.x! - shape.halfWidth!, shape.y! + shape.halfHeight!);
      ctx.lineTo(shape.x! + shape.halfWidth!, shape.y! + shape.halfHeight!);
      ctx.closePath();
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawEraserTrail() {
    const ctx = this.context;
    ctx.save();
    ctx.globalAlpha = 0.5; // Set transparency for the trail
    ctx.fillStyle = "rgba(0, 0, 0, 0.2)"; // Semi-transparent white
    for (const [x, y] of this.eraserTrail) {
      ctx.beginPath();
      ctx.arc(x, y, this.options.brushSize * 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private addAction(action: Action) {
    // Add screen dimensions for coordinate scaling
    const currentScreenDimensions = {
      width: this.canvas.clientWidth,
      height: this.canvas.clientHeight,
    };

    const actionWithDimensions = {
      ...action,
      screenDimensions: currentScreenDimensions,
    };

    this.sendToFirebase?.(actionWithDimensions);
  }

  private scaleActionForCurrentScreen(action: Action): Action {
    // If no screen dimensions or same role, return as is
    if (!action.screenDimensions || this.options.role === "tutor") {
      return action;
    }

    // Store tutor screen dimensions for consistent scaling
    if (this.options.role === "student" && action.screenDimensions) {
      this.tutorScreenDimensions = action.screenDimensions;
    }

    // If we don't have tutor dimensions, return as is
    if (!this.tutorScreenDimensions) {
      return action;
    }

    const currentScreen = {
      width: this.canvas.clientWidth,
      height: this.canvas.clientHeight,
    };

    // Calculate scaling ratios
    const scaleX = currentScreen.width / this.tutorScreenDimensions.width;
    const scaleY = currentScreen.height / this.tutorScreenDimensions.height;

    // Use uniform scaling to preserve aspect ratio
    const uniformScale = Math.min(scaleX, scaleY);

    // Calculate offset to center content when using uniform scaling
    const offsetX =
      (currentScreen.width - this.tutorScreenDimensions.width * uniformScale) /
      2;
    const offsetY =
      (currentScreen.height -
        this.tutorScreenDimensions.height * uniformScale) /
      2;

    // Debug logging
    // console.log('Canvas scaling debug:', {
    //   tutorDimensions: this.tutorScreenDimensions,
    //   currentDimensions: currentScreen,
    //   scaleX,
    //   scaleY,
    //   uniformScale,
    //   offsetX,
    //   offsetY
    // });

    // Clone the action to avoid modifying original
    const scaledAction = JSON.parse(JSON.stringify(action));

    // Scale coordinates based on element type
    if (scaledAction.element) {
      const element = scaledAction.element;

      if (element.type === "path") {
        element.points = element.points.map(([x, y]: [number, number]) => [
          x * uniformScale + offsetX,
          y * uniformScale + offsetY,
        ]);

        // Scale bounding box
        if (element.boundingBox) {
          element.boundingBox = [
            [
              element.boundingBox[0][0] * uniformScale + offsetX,
              element.boundingBox[0][1] * uniformScale + offsetY,
            ],
            [
              element.boundingBox[1][0] * uniformScale + offsetX,
              element.boundingBox[1][1] * uniformScale + offsetY,
            ],
          ];
        }
      } else if (
        element.type === "shape" ||
        element.type === "text" ||
        element.type === "image"
      ) {
        if (element.x !== undefined)
          element.x = element.x * uniformScale + offsetX;
        if (element.y !== undefined)
          element.y = element.y * uniformScale + offsetY;
        if (element.width !== undefined) element.width *= uniformScale;
        if (element.height !== undefined) element.height *= uniformScale;

        // For shapes, scale half dimensions
        if (element.type === "shape") {
          if (element.halfWidth !== undefined)
            element.halfWidth *= uniformScale;
          if (element.halfHeight !== undefined)
            element.halfHeight *= uniformScale;
        }
      }
    }

    // Scale camera pan coordinates with uniform scaling and offset
    if (scaledAction.cameraPan) {
      scaledAction.cameraPan = [
        scaledAction.cameraPan[0] * uniformScale + offsetX,
        scaledAction.cameraPan[1] * uniformScale + offsetY,
      ];
    }

    return scaledAction;
  }

  // private onResize() {
  //   if (!this.canvas || !this.context) return;

  //   const rect = this.canvas.getBoundingClientRect();
  //   this.options.screenWidth = rect.width;
  //   this.options.screenHeight = rect.height;
  //   this.canvas.width = rect.width;
  //   this.canvas.height = rect.height;

  //   this.redraw();
  // }

  public selectTool() {
    this.panMode = false;
    this.setCursor("default");
    this.currentTool = "select";
    this.setCurrentTool("select");
  }

  public penTool() {
    this.panMode = false;
    this.isPanning = false;
    this.setCursor("crosshair");
    this.currentTool = "pen";
    this.setCurrentTool("pen");
  }

  public handTool() {
    this.panMode = true;
    this.isPanning = false;
    this.setCursor("grab");
    this.currentTool = "hand";
    this.setCurrentTool("hand");
  }

  public scrollTool(type: "up" | "down") {
    if (type === "up") {
      this.panOffsetY -= 50; // Scroll up
    } else if (type === "down") {
      this.panOffsetY += 50; // Scroll down
    }
    this.redraw();
    this.addAction({
      type: "pan",
      timestamp: Date.now(),
      cameraPan: [0, this.panOffsetY],
      screenDimensions: {
        width: this.canvas.clientWidth,
        height: this.canvas.clientHeight,
      },
    });
  }
  public textTool() {
    this.panMode = false;
    this.currentTool = "text";
    this.setCursor("text");
    this.setCurrentTool("text");
  }
  public shapeTool() {
    this.panMode = false;
    this.currentTool = "shape";
    this.setCurrentTool("shape");
  }
  public insertShape(shape: "rectangle" | "circle" | "triangle") {
    this.shape = shape;
  }
  public eraserTool() {
    this.panMode = false;
    this.currentTool = "eraser";
    this.setCursor("default");
    this.setCurrentTool("eraser");
  }
  public undo() {
    const lastDeletion =
      this.deletedElements?.[this.deletedElements?.length - 1];
    if (lastDeletion?.source === "eraser") {
      const el = this.deletedElements.pop();
      this.elements.forEach((e) => {
        if (e.id === el?.id) {
          e.isDeleted = false;
        }
      });
      this.redraw();
    } else if (this.elements.length > 0) {
      const el = this.elements.pop();
      if (el) {
        this.deletedElements.push({ ...el, source: "undo" });
        this.addAction({
          type: "delete",
          timestamp: Date.now(),
          elementId: el.id,
          screenDimensions: {
            width: this.canvas.clientWidth,
            height: this.canvas.clientHeight,
          },
        });
      }
      this.redraw();
    }
  }
  public redo() {
    const lastDeletion =
      this.deletedElements?.[this.deletedElements?.length - 1];
    if (this.deletedElements.length > 0 && lastDeletion.source !== "eraser") {
      const el = this.deletedElements.pop();
      if (el?.source) delete (el as any).source;
      if (el) this.addElement(el);
      this.redraw();
    }
  }

  public reset() {
    this.options.brushSize = 2;
    this.lastPath = [];
    this.elements = [];
    this.imageCache = {};
    localStorage.removeItem("canvasElements");
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  public takeScreenshot() {}

  public imageTool() {
    this.panMode = false;
    this.currentTool = "image";
    this.setCursor("crosshair");
    this.setCurrentTool("image");
  }

  public insertImage(mouse: { x: number; y: number }) {
    if (this.currentTool !== "image") return;

    // Create a reusable file input element
    let fileInput = document.getElementById(
      "imageFileInput"
    ) as HTMLInputElement;
    if (!fileInput) {
      fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "image/*";
      fileInput.id = "imageFileInput";
      fileInput.style.display = "none";
      document.body.appendChild(fileInput);
    }

    // Trigger the file input click
    fileInput.click();

    // Handle file selection
    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      if (!file) return; // User canceled file selection

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file.");
        return;
      }

      const firebaseUrl = await this.uploadImgToFirebase(file);

      // Read the file as a base64 URL
      const reader = new FileReader();
      reader.onload = () => {
        const base64Url = reader.result as string;

        // Create an image element to get dimensions
        const img = new Image();
        img.src = base64Url;

        img.onload = () => {
          const imgWidth = img.naturalWidth;
          const imgHeight = img.naturalHeight;

          // Calculate canvas coordinates
          const x = mouse.x;
          const y = mouse.y - this.panOffsetY;

          // Add the image as an element for undo/redo support
          const imageElement: ImageElement = {
            type: "image",
            id: Date.now(),
            url: firebaseUrl,
            x,
            y,
            color: [0, 0, 0],
            width: imgWidth,
            height: imgHeight,
            boundingBox: [
              [x, x + imgWidth],
              [y, y + imgHeight],
            ],
            isDeleted: false,
          };
          this.addElement(imageElement);

          // Redraw the canvas
          this.redraw();
        };

        img.onerror = () => {
          alert("Failed to load the image. Please try again.");
        };
      };

      reader.onerror = () => {
        alert("Failed to read the file. Please try again.");
      };

      reader.readAsDataURL(file);
    };
  }
  public colorPicker(color: [number, number, number]) {
    this.options.color[0] = color[0];
    this.options.color[1] = color[1];
    this.options.color[2] = color[2];

    if (color[0] === 249 && color[1] === 250 && color[2] === 251) {
      this.options.brushSize = 15;
    } else {
      this.options.brushSize = 2;
    }
  }

  public fontSizePicker(size: number) {
    this.options.brushSize = size;
  }

  public resizeElement(amount: number, direction: "x" | "y") {
    if (
      !this.resizingElement ||
      !["image", "shape"].includes(this.resizingElement.type)
    )
      return;
    if (direction === "x") {
      this.resizingElement.width += amount;
    }
    if (direction === "y") {
      this.resizingElement.height += amount;
    }

    this.addAction({
      type: "update",
      timestamp: Date.now(),
      elementId: this.resizingElement.id,
      element: this.resizingElement,
    });

    this.redraw();
  }

  public snapToCurrentState(actions: Action[]) {
    const toDoActions = actions.filter((action) => action.type !== "pan");
    toDoActions.forEach((action) => {
      // Scale the action for current screen before applying
      const scaledAction = this.scaleActionForCurrentScreen(action);

      if (scaledAction.type === "draw" && scaledAction?.element) {
        this.elements.push(scaledAction.element);
      } else if (scaledAction.type === "delete" && scaledAction?.elementId) {
        this.elements.forEach((el) => {
          if (el.id === scaledAction.elementId) el.isDeleted = true;
        });
      } else if (scaledAction.type === "update" && scaledAction?.element) {
        this.elements = this.elements.map((el) => {
          if (el.id === scaledAction?.element?.id) return scaledAction.element;
          return el;
        });
      }
    });

    this.redraw();

    const lastPanAction = actions
      .filter((action) => action.type === "pan")
      .pop();

    if (lastPanAction) {
      // Scale the pan action as well
      const scaledPanAction = this.scaleActionForCurrentScreen(lastPanAction);
      this.panOffsetY = scaledPanAction.cameraPan![1];
    }

    this.redraw();
  }

  public async doAction(action: Action) {
    // Scale coordinates if this is a student receiving from tutor
    const scaledAction = this.scaleActionForCurrentScreen(action);

    if (scaledAction.type === "draw" && scaledAction?.element) {
      this.elements.push(scaledAction.element);
      if (scaledAction.element?.type === "path") {
        await this.drawAnimatedSmoothPath(scaledAction.element);
      }
      this.redraw();
    } else if (scaledAction.type === "delete" && scaledAction?.elementId) {
      this.elements.forEach((el) => {
        if (el.id === scaledAction.elementId) el.isDeleted = true;
      });
      this.redraw();
    } else if (scaledAction.type === "update" && scaledAction?.element) {
      this.elements = this.elements.map((el) => {
        if (el.id === scaledAction?.element?.id) return scaledAction.element;
        return el;
      });
      this.redraw();
    } else if (scaledAction.type === "pan" && scaledAction?.cameraPan) {
      // Smoothly animate to the new pan position
      const targetY = scaledAction.cameraPan[1];
      const currentY = this.panOffsetY;
      const distance = targetY - currentY;

      // If distance is small, just jump to target position
      if (Math.abs(distance) < 10) {
        this.panOffsetY = targetY;
        this.redraw();
      } else {
        // Smooth animation over 100ms with 10 steps
        const steps = 10;
        const stepSize = distance / steps;
        const stepDuration = 10; // 10ms per step = 100ms total

        for (let i = 1; i <= steps; i++) {
          setTimeout(() => {
            this.panOffsetY = currentY + stepSize * i;
            this.redraw();
          }, i * stepDuration);
        }
      }
    }
  }

  public destroy() {
    this.stopDrawing();
    // remove all listeners
    this.canvas.removeEventListener("wheel", this.onWheel.bind(this));
    this.canvas.removeEventListener("mousedown", this.onMouseDown.bind(this));
    this.canvas.removeEventListener("mousemove", this.onMouseMove.bind(this));
    this.canvas.removeEventListener("mouseout", this.onMouseUp.bind(this));
    window.removeEventListener("mouseup", this.onMouseUp.bind(this));
    window.removeEventListener("keydown", this.onKeyDown.bind(this));
    window.removeEventListener("keyup", this.onKeyUp.bind(this));
    // this.zoomAndPanInterval && clearInterval(this.zoomAndPanInterval);
  }
}
